import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/utils/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import UpgradeModal from "@/components/UpgradeModal";
import ConfirmDialog from "@/components/ConfirmDialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Edit, Trash2, Briefcase } from "lucide-react";

export default function Projects() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [projects, setProjects] = useState([]);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState("");
  const [customers, setCustomers] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, projectId: null });
  const [formData, setFormData] = useState({
    name: "",
    customer_id: "",
    description: "",
    hourly_rate: "",
    status: "active",
    budget_type: "",
    budget_value: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Sep 2026 — Filter „nur aktive Projekte" (User-Wunsch)
  const [onlyActive, setOnlyActive] = useState(false);

  useEffect(() => {
    fetchProjects();
    fetchCustomers();
    fetchTimeEntries();
  }, []);

  const openNewProjectDialog = () => {
    setEditingProject(null);
    setFormData({
      name: "",
      customer_id: "",
      description: "",
      hourly_rate: "",
      status: "active",
      budget_type: "",
      budget_value: "",
    });
    setDialogOpen(true);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("create") === "1") {
      openNewProjectDialog();
      params.delete("create");
      const nextSearch = params.toString();
      navigate({ search: nextSearch ? `?${nextSearch}` : "" }, { replace: true });
    }
  }, [location.search, navigate]);

  const fetchProjects = async () => {
    try {
      const response = await api.get('/projects');
      setProjects(response.data);
    } catch (error) {
      console.error("Error fetching projects:", error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers');
      setCustomers(response.data);
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  const fetchTimeEntries = async () => {
    try {
      const response = await api.get('/time-entries');
      setTimeEntries(response.data);
    } catch (error) {
      console.error("Error fetching time entries:", error);
    }
  };

  const calculateBudgetUsage = (project) => {
    if (!project.budget_type || !project.budget_value) return null;
    
    // Use net_duration_minutes (time minus pauses) for accurate project totals
    // Fall back to duration_minutes for backward compatibility with entries without pauses
    const projectTimeEntries = timeEntries.filter(e => e.project_id === project.id && (e.net_duration_minutes || e.duration_minutes));
    const totalMinutes = projectTimeEntries.reduce((sum, e) => sum + (e.net_duration_minutes || e.duration_minutes), 0);
    const totalHours = totalMinutes / 60;
    
    if (project.budget_type === "time") {
      const remaining = project.budget_value - totalHours;
      return {
        used: totalHours,
        total: project.budget_value,
        remaining: remaining,
        percentage: (totalHours / project.budget_value) * 100,
        label: `${totalHours.toFixed(1)}h / ${project.budget_value}h`,
        remainingLabel: remaining > 0 ? `${remaining.toFixed(1)}h verbleibend` : null,
        isOverBudget: totalHours > project.budget_value
      };
    } else {
      const totalCost = totalHours * project.hourly_rate;
      const remaining = project.budget_value - totalCost;
      return {
        used: totalCost,
        total: project.budget_value,
        remaining: remaining,
        percentage: (totalCost / project.budget_value) * 100,
        label: `${totalCost.toFixed(2)}€ / ${project.budget_value}€`,
        remainingLabel: remaining > 0 ? `${remaining.toFixed(2)}€ verbleibend` : null,
        isOverBudget: totalCost > project.budget_value
      };
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (isSubmitting) {
      console.log('Already submitting, ignoring duplicate request');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const data = {
        ...formData,
        hourly_rate: parseFloat(formData.hourly_rate) || 0,
        budget_value: formData.budget_value ? parseFloat(formData.budget_value) : null,
        budget_type: formData.budget_type || null,
      };

      if (editingProject) {
        await api.put(`/projects/${editingProject.id}`, data);
        toast.success('Projekt erfolgreich aktualisiert');
      } else {
        await api.post(`/projects`, data);
        toast.success('Projekt erfolgreich erstellt');
      }

      setDialogOpen(false);
      setEditingProject(null);
      setFormData({ name: "", customer_id: "", description: "", hourly_rate: "", status: "active", budget_type: "", budget_value: "" });
      fetchProjects();
      fetchTimeEntries();
    } catch (error) {
      if (error.response?.status === 403) {
        // Close dialog immediately so modal is visible
        setDialogOpen(false);
        setEditingProject(null);
        setFormData({ name: "", customer_id: "", description: "", hourly_rate: "", status: "active", budget_type: "", budget_value: "" });
        
        // Show upgrade modal
        setUpgradeMessage(error.response.data.detail);
        setUpgradeModalOpen(true);
      } else {
        console.error('Error saving project:', error);
        const errorMsg = error.response?.data?.detail || error.message || 'Error saving project';
        toast.error(errorMsg);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteProject = async (id) => {
    try {
      await api.delete(`/projects/${id}`);
      toast.success(t('projects.deleteSuccess', 'Projekt erfolgreich gelöscht'));
      fetchProjects();
      setConfirmDialog({ isOpen: false, projectId: null });
    } catch (error) {
      if (error.response?.status === 403) {
        setUpgradeMessage(error.response.data.detail);
        setUpgradeModalOpen(true);
      } else {
        toast.error(t('common.deleteError', 'Fehler beim Löschen'));
      }
    }
  };

  const handleDeleteClick = (id) => {
    setConfirmDialog({ isOpen: true, projectId: id });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: t('projects.active'), className: "bg-green-100 text-green-800" },
      completed: { label: t('projects.completed'), className: "bg-blue-100 text-blue-800" },
      archived: { label: "Archiviert", className: "bg-gray-100 text-gray-800" },
    };
    const config = statusConfig[status] || statusConfig.active;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  return (
    <div className="space-y-8" data-testid="projects-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-2" style={{ fontFamily: "Manrope, sans-serif" }}>Projekte</h1>
          <p className="text-lg text-gray-600">Verwalte deine Projekte</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-project-button" onClick={openNewProjectDialog}>
              <Plus className="h-4 w-4 mr-2" />
              Neues Projekt
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingProject ? t('projects.editProject') : t('projects.newProject')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>{t('projects.name')} *</Label>
                <Input
                  data-testid="project-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="z.B. Website Redesign"
                  required
                />
              </div>
              <div>
                <Label>{t('projects.customer')} *</Label>
                <Select
                  value={formData.customer_id}
                  onValueChange={(value) => setFormData({ ...formData, customer_id: value })}
                  required
                >
                  <SelectTrigger data-testid="customer-select">
                    <SelectValue placeholder={t('projects.selectCustomer')} />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('projects.description')}</Label>
                <Textarea
                  data-testid="project-description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('projects.description') + '...'}
                  rows={3}
                />
              </div>
              <div>
                <Label>{t('projects.hourlyRate')} (€)</Label>
                <Input
                  data-testid="hourly-rate-input"
                  type="number"
                  step="0.01"
                  value={formData.hourly_rate}
                  onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })}
                  placeholder="85.00"
                />
              </div>
              <div>
                <Label>{t('projects.status')}</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">{t('projects.active')}</SelectItem>
                    <SelectItem value="completed">{t('projects.completed')}</SelectItem>
                    <SelectItem value="archived">{t('projects.cancelled')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Budget Section */}
              <div className="space-y-3 pt-2 border-t">
                <Label className="text-base font-semibold">{t('projects.budget')} ({t('common.optional')})</Label>
                <div className="flex gap-4">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="budget_type"
                      value="time"
                      checked={formData.budget_type === "time"}
                      onChange={(e) => setFormData({ ...formData, budget_type: e.target.value, budget_value: "" })}
                      className="w-4 h-4 text-emerald-600"
                    />
                    <span>{t('projects.time')} ({t('projects.hours')})</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="budget_type"
                      value="money"
                      checked={formData.budget_type === "money"}
                      onChange={(e) => setFormData({ ...formData, budget_type: e.target.value, budget_value: "" })}
                      className="w-4 h-4 text-emerald-600"
                    />
                    <span>{t('projects.money')} (€)</span>
                  </label>
                  {formData.budget_type && (
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, budget_type: "", budget_value: "" })}
                      className="text-sm text-gray-500 hover:text-gray-700"
                    >
                      {t('common.reset')}
                    </button>
                  )}
                </div>
                
                {formData.budget_type && (
                  <div>
                    <Label>
                      {formData.budget_type === "time" ? t('projects.hours') : t('projects.budget') + ' (EUR)'}
                    </Label>
                    <Input
                      type="number"
                      step={formData.budget_type === "time" ? "0.5" : "0.01"}
                      value={formData.budget_value}
                      onChange={(e) => setFormData({ ...formData, budget_value: e.target.value })}
                      placeholder={formData.budget_type === "time" ? "z.B. 40" : "z.B. 5000"}
                      data-testid="budget-value-input"
                    />
                  </div>
                )}
              </div>
              
              <Button 
                type="submit" 
                className="w-full" 
                data-testid="save-project-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    {editingProject ? t('common.update') + '...' : t('common.create') + '...'}
                  </span>
                ) : (
                  editingProject ? t('common.update') : t('common.create')
                )}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Sep 2026 — Filter: nur aktive Projekte */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid="projects-only-active-toggle"
          onClick={() => setOnlyActive(!onlyActive)}
          className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
            onlyActive
              ? "border-green-300 bg-green-50 text-green-700 hover:bg-green-100"
              : "border-slate-200 bg-white text-slate-600 hover:border-green-300 hover:text-green-600"}`}
        >
          <Briefcase className="h-3.5 w-3.5" />
          Nur aktive Projekte
        </button>
        {onlyActive && (
          <span className="text-xs text-gray-500" data-testid="projects-active-filter-count">
            {projects.filter(p => (p.status || "active") === "active").length} von {projects.length} Projekten
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {(onlyActive ? projects.filter(p => (p.status || "active") === "active") : projects).map((project) => (
          <Card key={project.id} className="card-hover" data-testid={`project-card-${project.id}`}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3 flex-1">
                  <div className="p-2 bg-emerald-50 rounded-lg">
                    <Briefcase className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-lg">{project.name}</CardTitle>
                    <p className="text-sm text-gray-600 mt-1">{project.customer_name}</p>
                  </div>
                </div>
                {getStatusBadge(project.status)}
              </div>
            </CardHeader>
            <CardContent>
              {project.description && (
                <p className="text-sm text-gray-600 mb-4">{project.description}</p>
              )}
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-gray-600">Stundensatz:</span>
                <span className="font-semibold text-emerald-600">{project.hourly_rate.toFixed(2)} €/h</span>
              </div>
              
              {/* Budget Progress Bar */}
              {(() => {
                const budgetUsage = calculateBudgetUsage(project);
                if (!budgetUsage) return null;
                
                return (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-700">
                        {project.budget_type === "time" ? "Zeitbudget" : "Geldbudget"}
                      </span>
                      <span className={`text-sm font-semibold ${budgetUsage.isOverBudget ? 'text-red-600' : 'text-gray-900'}`}>
                        {budgetUsage.label}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div
                        className={`h-2.5 rounded-full transition-all ${
                          budgetUsage.isOverBudget ? 'bg-red-500' : 
                          budgetUsage.percentage > 80 ? 'bg-yellow-500' : 
                          'bg-emerald-500'
                        }`}
                        style={{ width: `${Math.min(budgetUsage.percentage, 100)}%` }}
                      ></div>
                    </div>
                    <div className="flex justify-between items-center mt-1.5">
                      {budgetUsage.isOverBudget ? (
                        <p className="text-xs text-red-600">Budget überschritten!</p>
                      ) : budgetUsage.remainingLabel ? (
                        <p className="text-xs text-emerald-600 font-medium">{budgetUsage.remainingLabel}</p>
                      ) : <span />}
                      <span className="text-xs text-gray-400">{Math.round(budgetUsage.percentage)}%</span>
                    </div>
                  </div>
                );
              })()}
              
              <div className="flex space-x-2">
                <Button
                  data-testid={`edit-project-${project.id}`}
                  size="sm"
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setEditingProject(project);
                    setFormData({
                      name: project.name,
                      customer_id: project.customer_id,
                      description: project.description || "",
                      hourly_rate: project.hourly_rate.toString(),
                      status: project.status,
                      budget_type: project.budget_type || "",
                      budget_value: project.budget_value?.toString() || "",
                    });
                    setDialogOpen(true);
                  }}
                >
                  <Edit className="h-4 w-4 mr-1" />
                  {t('common.edit')}
                </Button>
                <Button
                  data-testid={`delete-project-${project.id}`}
                  size="sm"
                  variant="outline"
                  onClick={() => handleDeleteClick(project.id)}
                >
                  <Trash2 className="h-4 w-4 text-red-600" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {projects.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            Noch keine Projekte vorhanden. Erstelle dein erstes Projekt!
          </CardContent>
        </Card>
      )}
      

    <UpgradeModal
      isOpen={upgradeModalOpen}
      onClose={() => setUpgradeModalOpen(false)}
      message={upgradeMessage}
      currentPlan={user?.plan}
    />

    <ConfirmDialog
      isOpen={confirmDialog.isOpen}
      onClose={() => setConfirmDialog({ isOpen: false, projectId: null })}
      onConfirm={() => deleteProject(confirmDialog.projectId)}
      title={t('projects.confirmDelete', 'Projekt löschen?')}
      description={t('projects.confirmDeleteDescription', 'Diese Aktion kann nicht rückgängig gemacht werden.')}
      confirmText={t('common.delete', 'Löschen')}
      cancelText={t('common.cancel', 'Abbrechen')}
      variant="destructive"
    />

    </div>
  );
}